import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
  useCallback,
  ReactNode,
} from 'react';

export type Language = 'en' | 'fr' | 'de' | 'es' | 'nl';

// Language modules loaded dynamically — only the active language is in memory
const loadedTranslations: Partial<Record<Language, Record<string, string>>> = {};

async function loadLanguage(lang: Language): Promise<Record<string, string>> {
  if (loadedTranslations[lang]) return loadedTranslations[lang]!;
  let mod: { default: Record<string, string> };
  switch (lang) {
    case 'nl':
      mod = await import('./i18n/nl.ts');
      break;
    case 'en':
    default:
      mod = await import('./i18n/en.ts');
      break;
  }
  loadedTranslations[lang] = mod.default;
  return mod.default;
}

interface I18nContextProps {
  t: (key: string, params?: Record<string, string>) => string;
  lang: Language;
  setLang: (lang: Language) => void;
}

const I18nContext = createContext<I18nContextProps | undefined>(undefined);

/**
 * Provider component that wraps the application and provides translation context.
 */
function detectLanguage(): Language {
  // 1. Check user account preference (set after login from database)
  try {
    const userData = localStorage.getItem('af_user');
    if (userData) {
      const user = JSON.parse(userData);
      if (user.preferred_language === 'nl' || user.preferred_language === 'en') {
        return user.preferred_language;
      }
    }
  } catch {
    /* ignored — storage may be unavailable */
  }
  // 2. Fallback: detect browser language
  const browserLang = navigator.language?.toLowerCase() || '';
  return browserLang.startsWith('nl') ? 'nl' : 'en';
}

export const I18nProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [lang, setLangState] = useState<Language>(detectLanguage);
  const [translations, setTranslations] = useState<Record<string, string>>({});
  const [fallback, setFallback] = useState<Record<string, string>>({});
  const [ready, setReady] = useState(false);

  // Load active language + EN fallback
  useEffect(() => {
    setReady(false);
    const promises: Promise<void>[] = [loadLanguage(lang).then(setTranslations)];
    if (lang !== 'en') {
      promises.push(loadLanguage('en').then(setFallback));
    }
    Promise.all(promises).then(() => setReady(true));
  }, [lang]);

  const setLang = useCallback((newLang: Language) => {
    setLangState(newLang);
  }, []);

  const t = useCallback(
    (key: string, params?: Record<string, string>) => {
      let value = translations[key] || fallback[key] || key;
      if (params) {
        for (const [k, v] of Object.entries(params)) {
          value = value.replace(new RegExp(`\\{${k}\\}`, 'g'), v);
        }
      }
      return value;
    },
    [translations, fallback]
  );

  const value = useMemo(() => ({ t, lang, setLang }), [t, lang, setLang]);

  // Don't render children until translations are loaded to prevent flash of raw keys
  if (!ready) return null;

  // Fix: Use React.createElement instead of JSX in a .ts file to avoid parser errors as I18nContext is not a namespace and JSX is not supported in .ts files.
  return React.createElement(I18nContext.Provider, { value: value }, children);
};

/**
 * Hook to access the translation function and language settings.
 */
export const useTranslation = () => {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error('useTranslation must be used within I18nProvider');
  }
  return context;
};

/**
 * Translation helper with fallback — use when a key might not exist yet.
 * Returns the fallback string if the key resolves to itself (missing translation).
 */
export function tt(t: (key: string) => string, key: string, fallback: string): string {
  const val = t(key);
  return val && val !== key ? val : fallback;
}
